import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import {
  User, Building2, Shield, Mic, Bell, Stethoscope, Settings, Key,
  Users, Coins, Plus, BarChart3, CheckCircle2, Send, Upload, FileText, Trash2, Eye, RefreshCw, Loader2,
  ArrowUpToLine, History, Undo2, AlertTriangle, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { getPreference, setPreferenceValue, STORAGE_KEY } from '../../components/shared/SendToBillingDialog';
import AccountSetupTab from '../../components/shared/AccountSetupTab';
import RolesPermissionsTab from '../../components/shared/RolesPermissionsTab';
import TeamManagement from '../TeamManagement';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClinicalPreferencesCard } from '../../components/lumina/ClinicalPreferencesCard';
import VisitTypeNoteConfigPanel from '../../components/shared/visitTypeNoteConfig/VisitTypeNoteConfigPanel';

const PROVIDERS = [
  { id: 1, name: 'Dr. Sarah Chen', specialty: 'Internal Medicine', status: 'active', notesThisMonth: 124, creditsUsed: 620 },
  { id: 2, name: 'Dr. James Wilson', specialty: 'Pain Management', status: 'active', notesThisMonth: 89, creditsUsed: 445 },
  { id: 3, name: 'Dr. Maria Santos', specialty: 'Cardiology', status: 'active', notesThisMonth: 67, creditsUsed: 335 },
  { id: 4, name: 'Dr. Robert Kim', specialty: 'Orthopedics', status: 'inactive', notesThisMonth: 0, creditsUsed: 0 },
];

const LuminaSettings = () => {
  const { user, authFetch } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'profile');
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [billingPref, setBillingPref] = useState(getPreference);
  const [ccdaImports, setCcdaImports] = useState([]);
  const [ccdaLoading, setCcdaLoading] = useState(false);
  const [ccdaViewRecord, setCcdaViewRecord] = useState(null);
  const [linkageStatuses, setLinkageStatuses] = useState({});
  const [deletingBatchId, setDeletingBatchId] = useState(null);

  // Absorption state
  const [absorptionMap, setAbsorptionMap] = useState({});
  const [absorbingId, setAbsorbingId] = useState(null);
  const [unabsorbConfirmId, setUnabsorbConfirmId] = useState(null);
  const [absHistoryOpen, setAbsHistoryOpen] = useState(false);
  const [absBatches, setAbsBatches] = useState([]);
  const [absBatchDetail, setAbsBatchDetail] = useState(null);
  const [absLoading, setAbsLoading] = useState(false);

  // Scan document history state
  const [scanHistory, setScanHistory] = useState([]);
  const [scanHistoryLoading, setScanHistoryLoading] = useState(false);
  const [deletingScanId, setDeletingScanId] = useState(null);

  // Section focus from URL (ccda/scan)
  const sectionParam = searchParams.get('section');

  const API = process.env.REACT_APP_BACKEND_URL || '';

  /**
   * Normalize patient-id extraction across all possible linkage payload shapes.
   * Checks the linkage result (primary) and the file object (fallback).
   * Returns the patient ID string or null if none found.
   */
  const getLinkedPatientId = (file, linkageResult) => {
    // Primary: linkage result from ccda_linkage_results
    if (linkageResult?.patient_id) return linkageResult.patient_id;
    if (linkageResult?.existing_patient_id) return linkageResult.existing_patient_id;
    if (linkageResult?.patient?.id) return linkageResult.patient.id;
    if (linkageResult?.linked_patient_id) return linkageResult.linked_patient_id;
    // Fallback: file object might embed linkage data
    if (file?.patient_id) return file.patient_id;
    if (file?.existing_patient_id) return file.existing_patient_id;
    if (file?.linkage?.patient_id) return file.linkage.patient_id;
    if (file?.linkage_result?.patient_id) return file.linkage_result.patient_id;
    if (file?.linkage?.patient?.id) return file.linkage.patient.id;
    if (file?.linkage_result?.patient?.id) return file.linkage_result.patient.id;
    if (file?.linked_patient_id) return file.linked_patient_id;
    if (file?.results?.patient_id) return file.results.patient_id;
    return null;
  };

  /** Is this linkage status one that indicates a successful link? */
  const isLinkedStatus = (status) => status === 'linked_existing' || status === 'created_new' || status === 'duplicate_document';

  /** Build resolver context from C-CDA file entry for patient ID resolution. */
  const buildResolverCtx = (fileEntry, batchId) => {
    const demo = fileEntry?.extracted?.demographics;
    const ctx = { ccda_batch_id: batchId, ccda_file_id: fileEntry?.file_id };
    if (demo?.name) {
      const parts = demo.name.trim().split(/\s+/);
      ctx.demographics = {
        first_name: parts[0] || '',
        last_name: parts.length > 1 ? parts[parts.length - 1] : '',
        date_of_birth: demo.dob || '',
      };
    }
    return ctx;
  };

  const fetchCcdaImports = useCallback(async () => {
    setCcdaLoading(true);
    try {
      const res = await authFetch(`${API}/api/ccda/batches`);
      if (res.ok) {
        const data = await res.json();
        const batches = data.batches || [];
        setCcdaImports(batches);
        const statuses = {};
        await Promise.all(batches.map(async (b) => {
          const bid = b.batch_id || b.id;
          if (!bid) return;
          try {
            const lr = await authFetch(`${API}/api/ccda/batch/${bid}/linkage-status`);
            if (lr.ok) {
              const ld = await lr.json();
              statuses[bid] = ld;
            }
          } catch {}
        }));
        setLinkageStatuses(statuses);
      } else {
        let detail = `Failed to load imports (${res.status})`;
        try { const body = await res.json(); detail = body.detail || detail; } catch {}
        toast.error(detail);
      }
    } catch {
      toast.error('Network error loading C-CDA imports');
    }
    setCcdaLoading(false);
  }, [authFetch, API]);

  const deleteCcdaImport = async (batchId) => {
    if (!batchId) { toast.error('Missing batch ID'); return; }
    if (deletingBatchId) return;
    setDeletingBatchId(batchId);
    try {
      const res = await authFetch(`${API}/api/ccda/batch/${batchId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Import record deleted');
        if (ccdaViewRecord?.batch_id === batchId || ccdaViewRecord?.id === batchId) setCcdaViewRecord(null);
        await fetchCcdaImports();
      } else {
        let detail = `Delete failed (${res.status})`;
        try { const body = await res.json(); detail = body.detail || detail; } catch {}
        toast.error(detail);
      }
    } catch {
      toast.error('Network error — could not delete');
    } finally {
      setDeletingBatchId(null);
    }
  };

  useEffect(() => { if (activeTab === 'documents') { fetchCcdaImports(); fetchAbsorptionStatuses(); fetchScanHistory(); } }, [activeTab, fetchCcdaImports, fetchAbsorptionStatuses, fetchScanHistory]);

  // Auto-scroll to section when navigated from import modal
  useEffect(() => {
    if (activeTab === 'documents' && sectionParam) {
      const timer = setTimeout(() => {
        const el = document.getElementById(`doc-section-${sectionParam}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [activeTab, sectionParam]);

  // Fetch scan document history
  const fetchScanHistory = useCallback(async () => {
    setScanHistoryLoading(true);
    try {
      const res = await authFetch(`${API}/api/patient-documents/history`);
      if (res.ok) {
        const data = await res.json();
        setScanHistory(data.records || []);
      } else {
        setScanHistory([]);
      }
    } catch {
      setScanHistory([]);
    }
    setScanHistoryLoading(false);
  }, [authFetch, API]);

  // Delete scan history record
  const handleDeleteScanRecord = async (recordId) => {
    try {
      const res = await authFetch(`${API}/api/patient-documents/history/${recordId}`, { method: 'DELETE' });
      if (res.ok) {
        setScanHistory(prev => prev.filter(r => r.id !== recordId));
        toast.success('Record removed from history');
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to delete record');
      }
    } catch {
      toast.error('Network error deleting record');
    }
    setDeletingScanId(null);
  };

  // ── Absorption helpers ──
  const fetchAbsorptionStatuses = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/analyses/absorption-batches`);
      if (!res.ok) return;
      const data = await res.json();
      const batches = data.batches || [];
      const map = {};
      for (const batch of batches) {
        for (const item of (batch.items || [])) {
          const aid = item.analysis_id;
          if (aid && item.status === 'absorbed') {
            map[aid] = {
              status: item.status,
              batch_id: batch.batch_id || batch.id,
              patient_id: item.patient_id,
              absorbed_at: item.absorbed_at,
              sections: item.sections || [],
              id_source: item.id_source,
              source: batch.source,
            };
          }
        }
      }
      setAbsorptionMap(map);
    } catch { /* network error — silent */ }
  }, [authFetch, API]);

  const handleViewChart = async (patientId, resolverCtx) => {
    if (!patientId) {
      toast.error('No patient ID available for this record');
      return;
    }
    try {
      const res = await authFetch(`${API}/api/patients/${patientId}`);
      if (res.ok) {
        navigate(`/lumina/clinical-focus?patientId=${patientId}`);
        return;
      }

      // Direct lookup failed — try canonical resolver
      const resolveBody = { patient_ref: patientId };
      if (resolverCtx?.demographics) resolveBody.demographics = resolverCtx.demographics;
      if (resolverCtx?.ccda_batch_id) resolveBody.ccda_batch_id = resolverCtx.ccda_batch_id;
      if (resolverCtx?.ccda_file_id) resolveBody.ccda_file_id = resolverCtx.ccda_file_id;

      try {
        const rr = await authFetch(`${API}/api/patients/resolve-id`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(resolveBody),
        });
        if (rr.ok) {
          const rd = await rr.json();
          if (rd.resolved_patient_id) {
            navigate(`/lumina/clinical-focus?patientId=${rd.resolved_patient_id}`);
            return;
          }
        }
      } catch { /* resolver network error — fall through to toast */ }

      toast.error('Could not resolve patient for this record', {
        description: `ID "${patientId}" not found. The ID format may not match the current patient system.`,
        action: { label: 'Open Patients', onClick: () => navigate('/lumina/patients') },
      });
    } catch {
      navigate(`/lumina/clinical-focus?patientId=${patientId}`);
    }
  };

  const handleAbsorb = async (fileId, patientId, ccdaBatchId, resolverCtx) => {
    if (absorbingId) return;
    if (!patientId) {
      toast.error('Cannot absorb: no patient ID resolved', { description: 'Link the file to a patient first, or re-upload with patient matching.' });
      return;
    }
    setAbsorbingId(fileId);

    // Pre-resolve patient ID for correctness
    let finalPid = patientId;
    try {
      const resolveBody = { patient_ref: patientId };
      if (resolverCtx?.demographics) resolveBody.demographics = resolverCtx.demographics;
      if (ccdaBatchId) resolveBody.ccda_batch_id = ccdaBatchId;
      if (resolverCtx?.ccda_file_id) resolveBody.ccda_file_id = resolverCtx.ccda_file_id;
      const rr = await authFetch(`${API}/api/patients/resolve-id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(resolveBody),
      });
      if (rr.ok) {
        const rd = await rr.json();
        if (rd.resolved_patient_id) finalPid = rd.resolved_patient_id;
      }
    } catch { /* resolver failed — use original ID */ }

    console.info('[absorb] file=%s patient=%s (resolved from %s) batch=%s', fileId, finalPid, patientId, ccdaBatchId);
    try {
      const res = await authFetch(`${API}/api/analyses/absorb-to-chart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysis_id: fileId,
          patient_id: finalPid,
          ccda_batch_id: ccdaBatchId,
          source: 'ccda_file_adapter',
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.already_absorbed) {
          toast.info('Already absorbed to chart');
        } else {
          const secCount = (data.items?.[0]?.sections || []).length;
          toast.success(`Absorbed to chart${secCount ? ` — ${secCount} sections` : ''}`, {
            action: finalPid ? { label: 'View Chart', onClick: () => handleViewChart(finalPid) } : undefined,
          });
        }
        await fetchAbsorptionStatuses();
      } else {
        let detail = `Absorption failed (${res.status})`;
        try { const body = await res.json(); detail = body.detail || detail; } catch {}
        toast.error(detail);
      }
    } catch {
      toast.error('Network error — could not absorb');
    } finally {
      setAbsorbingId(null);
    }
  };

  const handleUnabsorb = async (analysisId) => {
    setUnabsorbConfirmId(null);
    setAbsorbingId(analysisId);
    try {
      const res = await authFetch(`${API}/api/analyses/${analysisId}/unabsorb`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        toast.success('Unabsorbed from chart');
        await fetchAbsorptionStatuses();
      } else {
        let detail = `Unabsorb failed (${res.status})`;
        try { const body = await res.json(); detail = body.detail || detail; } catch {}
        toast.error(detail);
      }
    } catch {
      toast.error('Network error — could not unabsorb');
    } finally {
      setAbsorbingId(null);
    }
  };

  const fetchAbsorptionHistory = async () => {
    setAbsLoading(true);
    try {
      const res = await authFetch(`${API}/api/analyses/absorption-batches`);
      if (res.ok) {
        const data = await res.json();
        setAbsBatches(data.batches || []);
      } else {
        toast.error('Failed to load absorption history');
      }
    } catch {
      toast.error('Network error loading absorption history');
    }
    setAbsLoading(false);
  };

  const fetchAbsBatchDetail = async (batchId) => {
    try {
      const res = await authFetch(`${API}/api/analyses/absorption-batches/${batchId}`);
      if (res.ok) {
        const data = await res.json();
        setAbsBatchDetail(data);
      } else {
        toast.error('Failed to load batch detail');
      }
    } catch {
      toast.error('Network error loading batch detail');
    }
  };

  const totalNotes = PROVIDERS.reduce((s, p) => s + p.notesThisMonth, 0);
  const totalCredits = PROVIDERS.reduce((s, p) => s + p.creditsUsed, 0);

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account, clinic, and preferences</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="profile" className="gap-2"><User className="h-4 w-4" /> Profile</TabsTrigger>
          <TabsTrigger value="clinic" className="gap-2"><Building2 className="h-4 w-4" /> Clinic</TabsTrigger>
          <TabsTrigger value="preferences" className="gap-2"><Settings className="h-4 w-4" /> Preferences</TabsTrigger>
          <TabsTrigger value="roles" className="gap-2" data-testid="tab-roles"><Shield className="h-4 w-4" /> Roles</TabsTrigger>
          <TabsTrigger value="teams" className="gap-2" data-testid="tab-teams"><Users className="h-4 w-4" /> Teams</TabsTrigger>
          <TabsTrigger value="setup" className="gap-2" data-testid="tab-setup"><Upload className="h-4 w-4" /> Account Setup</TabsTrigger>
          <TabsTrigger value="documents" className="gap-2" data-testid="tab-documents"><FileText className="h-4 w-4" /> Documents</TabsTrigger>
          <TabsTrigger value="security" className="gap-2"><Shield className="h-4 w-4" /> Security</TabsTrigger>
        </TabsList>

        {/* Profile */}
        <TabsContent value="profile" className="space-y-6 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base font-semibold">Personal Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>First Name</Label><Input defaultValue={user?.first_name || 'Sarah'} data-testid="settings-first-name" /></div>
                <div className="space-y-2"><Label>Last Name</Label><Input defaultValue={user?.last_name || 'Chen'} data-testid="settings-last-name" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Email</Label><Input defaultValue={user?.email || 'sarah.chen@clinic.com'} data-testid="settings-email" /></div>
                <div className="space-y-2"><Label>Phone</Label><Input defaultValue={user?.phone || '(555) 123-4567'} data-testid="settings-phone" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Specialty</Label>
                  <Select defaultValue="internal_medicine">
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="internal_medicine">Internal Medicine</SelectItem>
                      <SelectItem value="pain_management">Pain Management</SelectItem>
                      <SelectItem value="cardiology">Cardiology</SelectItem>
                      <SelectItem value="orthopedics">Orthopedics</SelectItem>
                      <SelectItem value="psychiatry">Psychiatry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>NPI Number</Label><Input defaultValue="1234567890" /></div>
              </div>
              <Button className="bg-black hover:bg-gray-800" onClick={() => toast.success('Profile saved')}>Save Changes</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clinic — Multi-Provider Mode */}
        <TabsContent value="clinic" className="space-y-6 mt-4">
          {/* Clinic Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total Providers</p><p className="text-2xl font-bold mt-1">{PROVIDERS.filter(p => p.status === 'active').length}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Notes This Month</p><p className="text-2xl font-bold mt-1">{totalNotes}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Credits Used</p><p className="text-2xl font-bold text-amber-600 mt-1">{totalCredits}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Monthly Spend</p><p className="text-2xl font-bold mt-1">${totalCredits.toLocaleString()}</p></CardContent></Card>
          </div>

          {/* Clinic Info */}
          <Card>
            <CardHeader><CardTitle className="text-base font-semibold">Clinic Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Clinic Name</Label><Input defaultValue="Springfield Medical Associates" /></div>
                <div className="space-y-2"><Label>Tax ID</Label><Input defaultValue="12-3456789" /></div>
              </div>
              <div className="space-y-2"><Label>Address</Label><Input defaultValue="100 Medical Center Dr, Springfield, IL 62701" /></div>
              <Button className="bg-black hover:bg-gray-800" onClick={() => toast.success('Clinic info saved')}>Save</Button>
            </CardContent>
          </Card>

          {/* Provider Management */}
          <Card data-testid="provider-management">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold">Providers</CardTitle>
                <CardDescription>Manage clinic providers and credit allocation</CardDescription>
              </div>
              <Button size="sm" className="bg-black hover:bg-gray-800 gap-1" onClick={() => setShowAddProvider(true)} data-testid="add-provider-btn">
                <Plus className="h-3 w-3" /> Add Provider
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead>Specialty</TableHead>
                    <TableHead className="text-right">Notes (MTD)</TableHead>
                    <TableHead className="text-right">Credits Used</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {PROVIDERS.map(p => (
                    <TableRow key={p.id} data-testid={`provider-row-${p.id}`}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="text-muted-foreground">{p.specialty}</TableCell>
                      <TableCell className="text-right font-medium">{p.notesThisMonth}</TableCell>
                      <TableCell className="text-right">{p.creditsUsed}</TableCell>
                      <TableCell>
                        <Badge className={p.status === 'active' ? 'bg-violet-50 text-violet-700 text-xs' : 'bg-slate-100 text-slate-500 text-xs'}>
                          {p.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Credit Allocation */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2"><Coins className="h-4 w-4 text-amber-500" /> Credit Allocation</CardTitle>
              <CardDescription>Set monthly credit limits per provider</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {PROVIDERS.filter(p => p.status === 'active').map(p => (
                <div key={p.id} className="flex items-center justify-between p-3 rounded-lg border">
                  <span className="text-sm font-medium">{p.name}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">{p.creditsUsed} / </span>
                    <Input className="w-24 h-8 text-sm" defaultValue="1000" />
                    <span className="text-xs text-muted-foreground">credits/mo</span>
                  </div>
                </div>
              ))}
              <Button className="bg-black hover:bg-gray-800 mt-2" onClick={() => toast.success('Credit limits saved')}>Save Allocation</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preferences */}
        <TabsContent value="preferences" className="space-y-6 mt-4">
          {/* M5 — Clinical Preferences (specialty, signals, note format,
               per-visit-type routing, templates, learning engine — fully
               consolidated under this card so providers configure all
               note + visit-type behavior in one place. */}
          <ClinicalPreferencesCard />

          {/* Display Preferences — Font Size */}
          <Card data-testid="display-prefs-card">
            <CardHeader><CardTitle className="text-base font-semibold">Display Preferences</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg border" data-testid="font-size-setting">
                <div><p className="text-sm font-medium">Font Size</p><p className="text-xs text-muted-foreground">Adjust text size across the application</p></div>
                <div className="flex gap-1">
                  {[{ key: 'small', label: 'Small' }, { key: 'medium', label: 'Medium' }, { key: 'large', label: 'Large' }].map(s => (
                    <Button key={s.key} size="sm" variant={(localStorage.getItem('lumina-font-size') || 'small') === s.key ? 'default' : 'outline'}
                      className={cn("text-xs h-7 px-3", (localStorage.getItem('lumina-font-size') || 'small') === s.key && "bg-slate-900")}
                      onClick={() => {
                        const root = document.documentElement;
                        root.setAttribute('data-font-size', s.key);
                        root.classList.remove('font-size-small', 'font-size-medium', 'font-size-large');
                        root.classList.add(`font-size-${s.key}`);
                        localStorage.setItem('lumina-font-size', s.key);
                        toast.success(`Font size set to ${s.label}`);
                      }}
                      data-testid={`font-${s.key}`}>
                      {s.label}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base font-semibold">Documentation Preferences</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><p className="text-sm font-medium">Default Specialty Template</p><p className="text-xs text-muted-foreground">Auto-select template when creating notes</p></div>
                <Select defaultValue="primary_care">
                  <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary_care">Primary Care</SelectItem>
                    <SelectItem value="pain_management">Pain Management</SelectItem>
                    <SelectItem value="orthopedics">Orthopedics</SelectItem>
                    <SelectItem value="cardiology">Cardiology</SelectItem>
                    <SelectItem value="psychiatry">Psychiatry</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><p className="text-sm font-medium">Auto-suggest ICD/CPT Codes</p><p className="text-xs text-muted-foreground">Show billing code suggestions after note generation</p></div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><p className="text-sm font-medium">Generate Patient Instructions</p><p className="text-xs text-muted-foreground">Auto-create patient-facing visit summary</p></div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><p className="text-sm font-medium">Auto Follow-Up Tasks</p><p className="text-xs text-muted-foreground">Detect and suggest follow-up actions from the plan</p></div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><p className="text-sm font-medium">AI Chart Prep</p><p className="text-xs text-muted-foreground">Auto-generate patient brief before visits</p></div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><p className="text-sm font-medium">Voice Commands</p><p className="text-xs text-muted-foreground">Enable hands-free voice commands during ambient listening</p></div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border" data-testid="billing-nav-preference">
                <div className="flex items-center gap-3">
                  <Send className="h-4 w-4 text-slate-500" />
                  <div><p className="text-sm font-medium">Send to Billing Navigation</p><p className="text-xs text-muted-foreground">What happens when you send a note to revQ Billing</p></div>
                </div>
                <Select value={billingPref} onValueChange={(val) => { setBillingPref(val); setPreferenceValue(val); toast.success('Billing preference updated'); }}>
                  <SelectTrigger className="w-48" data-testid="billing-pref-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="always_ask">Always ask me</SelectItem>
                    <SelectItem value="always_go">Always open revQ</SelectItem>
                    <SelectItem value="always_stay">Always stay in Lumina</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base font-semibold">Notifications</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><p className="text-sm font-medium">Note Completion Alerts</p><p className="text-xs text-muted-foreground">Notify when SOAP note is ready for review</p></div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><p className="text-sm font-medium">Low Credit Warning</p><p className="text-xs text-muted-foreground">Alert when credit balance falls below 50</p></div>
                <Switch defaultChecked />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles & Permissions */}
        <TabsContent value="roles" className="mt-4">
          <RolesPermissionsTab appKey="lumina" />
        </TabsContent>

        {/* Teams */}
        <TabsContent value="teams" className="mt-4">
          <TeamManagement />
        </TabsContent>

        {/* Account Setup */}
        <TabsContent value="setup" className="mt-4">
          <AccountSetupTab appKey="lumina" />
        </TabsContent>

        {/* Security */}
        <TabsContent value="security" className="space-y-6 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base font-semibold">Security & Compliance</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg border border-violet-200 bg-violet-50/30">
                <div className="flex items-center gap-3"><Shield className="h-5 w-5 text-violet-600" /><div><p className="text-sm font-medium">HIPAA Compliance</p><p className="text-xs text-muted-foreground">All data is encrypted and HIPAA compliant</p></div></div>
                <Badge className="bg-violet-100 text-violet-700"><CheckCircle2 className="h-3 w-3 mr-1" /> Active</Badge>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><p className="text-sm font-medium">Two-Factor Authentication</p><p className="text-xs text-muted-foreground">Add an extra layer of security</p></div>
                <Switch />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><p className="text-sm font-medium">Session Timeout</p><p className="text-xs text-muted-foreground">Auto-logout after inactivity</p></div>
                <Select defaultValue="30">
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">60 minutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Change Password</Label>
                <div className="grid grid-cols-2 gap-4">
                  <Input type="password" placeholder="New password" />
                  <Input type="password" placeholder="Confirm password" />
                </div>
              </div>
              <Button className="bg-black hover:bg-gray-800" onClick={() => toast.success('Security settings updated')}>Save</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents — C-CDA Import History + Scan History */}
        <TabsContent value="documents" className="space-y-6 mt-4" data-testid="documents-tab">
          <div id="doc-section-ccda">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-semibold">C-CDA Import History</CardTitle>
                  <CardDescription className="text-xs mt-1">Review, view, or delete past C-CDA document imports</CardDescription>
                </div>
                <Button variant="outline" size="sm" className="gap-2" onClick={() => { setAbsHistoryOpen(true); fetchAbsorptionHistory(); }} data-testid="absorption-history-btn">
                  <History className="h-3.5 w-3.5" /> Absorption History
                </Button>
                <Button variant="outline" size="sm" className="gap-2" onClick={fetchCcdaImports} data-testid="refresh-imports-btn">
                  <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary pills */}
              <div className="flex items-center gap-2 mb-4">
                {[
                  { label: 'Batches', val: ccdaImports.length, color: '#6b5ce7' },
                  { label: 'Total Files', val: ccdaImports.reduce((s, r) => s + (r.total_files || 0), 0), color: '#3b82f6' },
                  { label: 'Completed', val: ccdaImports.filter(r => r.status === 'completed').length, color: '#22c55e' },
                  { label: 'Failed', val: ccdaImports.filter(r => r.status === 'failed').length, color: '#ef4444' },
                ].map(p => (
                  <span key={p.label} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, border: '0.5px solid #e8e8e8', background: '#fafafa' }}>
                    <strong style={{ color: p.color }}>{p.val}</strong> <span style={{ color: '#888' }}>{p.label}</span>
                  </span>
                ))}
              </div>

              {ccdaLoading ? (
                <div className="flex justify-center py-8"><div className="h-5 w-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" /></div>
              ) : ccdaImports.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No C-CDA imports yet</p>
                  <p className="text-xs text-slate-400 mt-1">Import a C-CDA file from the Patients page to see history here</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Batch ID</TableHead>
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                      <TableHead className="text-xs">Files</TableHead>
                      <TableHead className="text-xs">Parsed</TableHead>
                      <TableHead className="text-xs">Failed</TableHead>
                      <TableHead className="text-xs">Linkage</TableHead>
                      <TableHead className="text-xs">Absorption</TableHead>
                      <TableHead className="text-xs text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ccdaImports.map(rec => {
                      const bid = rec.batch_id || rec.id;
                      const ls = linkageStatuses[bid];
                      return (
                      <TableRow key={bid} data-testid={`ccda-row-${bid}`}>
                        <TableCell>
                          <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#6b5ce7', fontWeight: 500 }}>{bid}</span>
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">
                          {rec.created_at ? new Date(rec.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn('text-[10px]',
                            rec.status === 'completed' ? 'border-green-200 bg-green-50 text-green-700' :
                            rec.status === 'failed' ? 'border-red-200 bg-red-50 text-red-700' :
                            rec.status === 'partial' ? 'border-amber-200 bg-amber-50 text-amber-700' :
                            'border-yellow-200 bg-yellow-50 text-yellow-700'
                          )}>
                            {rec.status === 'completed' ? 'Completed' : rec.status === 'failed' ? 'Failed' : rec.status === 'partial' ? 'Partial' : rec.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{rec.total_files || 0}</TableCell>
                        <TableCell className="text-xs text-green-700">{rec.completed || 0}</TableCell>
                        <TableCell className="text-xs text-red-600">{rec.failed || 0}</TableCell>
                        <TableCell>
                          {ls?.linked ? (
                            <div className="flex items-center gap-1 flex-wrap" data-testid={`linkage-status-${bid}`}>
                              {(ls.linked_existing || 0) > 0 && <Badge variant="outline" className="text-[9px] px-1 h-4 border-blue-200 text-blue-700">{ls.linked_existing} linked</Badge>}
                              {(ls.created_new || 0) > 0 && <Badge variant="outline" className="text-[9px] px-1 h-4 border-green-200 text-green-700">{ls.created_new} new</Badge>}
                              {(ls.duplicate_documents || 0) > 0 && <Badge variant="outline" className="text-[9px] px-1 h-4 border-amber-200 text-amber-700">{ls.duplicate_documents} dup</Badge>}
                              {(ls.failed || 0) > 0 && <Badge variant="outline" className="text-[9px] px-1 h-4 border-red-200 text-red-700">{ls.failed} failed</Badge>}
                            </div>
                          ) : (
                            <span className="text-[10px] text-slate-400 italic" data-testid={`not-linked-${bid}`}>Not Linked Yet</span>
                          )}
                        </TableCell>
                        <TableCell data-testid={`absorption-agg-${bid}`}>
                          {(() => {
                            const batchFiles = rec.files || [];
                            const absorbedCount = batchFiles.filter(f => absorptionMap[f.file_id]?.status === 'absorbed').length;
                            const eligibleCount = batchFiles.filter(f => {
                              if (f.status !== 'completed') return false;
                              const lr2 = ls?.results?.find(r => r.file_id === f.file_id);
                              return !!getLinkedPatientId(f, lr2);
                            }).length;
                            if (eligibleCount === 0) return <span className="text-[10px] text-slate-400 italic">N/A</span>;
                            return (
                              <Badge variant="outline" className={cn('text-[9px] px-1.5 h-4',
                                absorbedCount === eligibleCount ? 'border-green-200 text-green-700 bg-green-50' :
                                absorbedCount > 0 ? 'border-blue-200 text-blue-700 bg-blue-50' :
                                'border-slate-200 text-slate-500'
                              )}>
                                {absorbedCount}/{eligibleCount} absorbed
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center gap-1 justify-end">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); setCcdaViewRecord(rec); }} data-testid={`view-${bid}`}>
                              <Eye className="h-3.5 w-3.5 text-slate-500" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                              disabled={deletingBatchId === bid}
                              onClick={(e) => { e.stopPropagation(); deleteCcdaImport(bid); }}
                              data-testid={`delete-${bid}`}>
                              {deletingBatchId === bid
                                ? <Loader2 className="h-3.5 w-3.5 text-slate-400 animate-spin" />
                                : <Trash2 className="h-3.5 w-3.5 text-red-400" />}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );})}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          </div>

          {/* ── Scan / Document Processing History ── */}
          <div id="doc-section-scan">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Eye className="h-4 w-4 text-violet-500" /> Scan / Document Processing History
                  </CardTitle>
                  <CardDescription className="text-xs mt-1">ID cards, insurance cards, and other scanned documents processed through Lens</CardDescription>
                </div>
                <Button variant="outline" size="sm" className="gap-2" onClick={fetchScanHistory} data-testid="refresh-scan-history-btn">
                  <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary pills */}
              <div className="flex items-center gap-2 mb-4">
                {[
                  { label: 'Total Scans', val: scanHistory.length, color: '#6b5ce7' },
                  { label: 'Processing', val: scanHistory.filter(r => r.status === 'processing').length, color: '#f59e0b' },
                  { label: 'Completed', val: scanHistory.filter(r => r.status === 'completed').length, color: '#22c55e' },
                  { label: 'Failed', val: scanHistory.filter(r => r.status === 'failed').length, color: '#ef4444' },
                ].map(p => (
                  <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#fafafa', border: '0.5px solid #ebebeb', borderRadius: 6, padding: '3px 8px' }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: p.color }} />
                    <span style={{ fontSize: 11, color: '#888' }}>{p.label}:</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#111' }}>{p.val}</span>
                  </div>
                ))}
              </div>
              {scanHistoryLoading ? (
                <div className="flex items-center justify-center py-8 gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading scan history...
                </div>
              ) : scanHistory.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground" data-testid="scan-history-empty">
                  <Eye className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p>No scanned documents yet</p>
                  <p className="text-xs mt-1">Documents scanned through the Import modal or Lens will appear here</p>
                </div>
              ) : (
                <Table data-testid="scan-history-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Filename</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Analysis ID</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Uploaded</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scanHistory.map((record, idx) => (
                      <TableRow key={record.id || idx} data-testid={`scan-history-row-${idx}`}>
                        <TableCell className="font-medium text-xs">{record.filename || 'Unknown'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn("text-[10px]",
                            record.status === 'completed' ? 'border-green-200 text-green-700 bg-green-50' :
                            record.status === 'failed' ? 'border-red-200 text-red-700 bg-red-50' :
                            'border-amber-200 text-amber-700 bg-amber-50'
                          )}>
                            {record.status || 'processing'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs font-mono text-muted-foreground">{record.analysis_id || '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{record.content_type || '—'}</TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground">
                          {record.timestamp ? new Date(record.timestamp).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell>
                          <button onClick={() => setDeletingScanId(record.id)}
                            className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                            data-testid={`delete-scan-record-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Scan History Delete Confirm Dialog */}
      <Dialog open={!!deletingScanId} onOpenChange={(v) => { if (!v) setDeletingScanId(null); }}>
        <DialogContent className="max-w-sm" data-testid="delete-scan-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">Remove from history?</DialogTitle>
            <DialogDescription className="text-xs">
              This will remove the scan record from your local history. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDeletingScanId(null)}>Cancel</Button>
            <Button variant="destructive" size="sm" className="gap-1.5" data-testid="confirm-delete-scan-btn"
              onClick={() => handleDeleteScanRecord(deletingScanId)}>
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CCDA View Record Dialog */}
      <Dialog open={!!ccdaViewRecord} onOpenChange={(v) => !v && setCcdaViewRecord(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="ccda-view-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              C-CDA Batch — {ccdaViewRecord?.batch_id}
            </DialogTitle>
            <DialogDescription>
              Imported on {ccdaViewRecord?.created_at ? new Date(ccdaViewRecord.created_at).toLocaleString() : '—'}
              {' · '}{ccdaViewRecord?.total_files || 0} files · {ccdaViewRecord?.completed || 0} parsed · {ccdaViewRecord?.failed || 0} failed
            </DialogDescription>
          </DialogHeader>
          {ccdaViewRecord?.files?.length > 0 ? (
            <div className="space-y-3 mt-2">
              {ccdaViewRecord.files.map((f, i) => {
                const ls = linkageStatuses[ccdaViewRecord.batch_id];
                const lr = ls?.results?.find(r => r.file_id === f.file_id);
                return (
                <div key={f.file_id || i} className="border rounded-lg overflow-hidden">
                  <div className={cn("flex items-center justify-between px-3 py-2",
                    f.status === 'completed' ? "bg-green-50" : f.status === 'failed' ? "bg-red-50" : "bg-slate-50"
                  )}>
                    <div className="flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-slate-500" />
                      <span className="text-xs font-medium text-slate-700">{f.filename}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className={cn('text-[9px] px-1 h-4',
                        f.status === 'completed' ? 'border-green-200 text-green-700 bg-green-50' :
                        f.status === 'failed' ? 'border-red-200 text-red-700 bg-red-50' : 'border-slate-200 text-slate-500'
                      )} data-testid={`parse-badge-${f.file_id}`}>{f.status === 'completed' ? 'Parsed' : f.status === 'failed' ? 'Parse Failed' : f.status}</Badge>
                      {lr ? (
                        <Badge variant="outline" className={cn('text-[9px] px-1 h-4',
                          lr.linkage_status === 'linked_existing' ? 'border-blue-200 text-blue-700 bg-blue-50' :
                          lr.linkage_status === 'created_new' ? 'border-green-200 text-green-700 bg-green-50' :
                          lr.linkage_status === 'duplicate_document' ? 'border-amber-200 text-amber-700 bg-amber-50' :
                          lr.linkage_status === 'failed' ? 'border-red-200 text-red-700 bg-red-50' : 'border-slate-200 text-slate-500'
                        )} data-testid={`linkage-badge-${f.file_id}`}>
                          {lr.linkage_status === 'linked_existing' ? 'Linked' :
                           lr.linkage_status === 'created_new' ? 'Created' :
                           lr.linkage_status === 'duplicate_document' ? 'Dup Doc' :
                           lr.linkage_status === 'failed' ? 'Link Failed' : lr.linkage_status}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[9px] px-1 h-4 border-slate-200 text-slate-400" data-testid={`linkage-badge-none-${f.file_id}`}>Not Linked</Badge>
                      )}
                    </div>
                  </div>
                  {/* ── Patient ID row (uses normalized helper) ── */}
                  {(() => {
                    const pid = getLinkedPatientId(f, lr);
                    const linked = lr && isLinkedStatus(lr.linkage_status);
                    const isDup = lr?.linkage_status === 'duplicate_document';
                    if (pid) return (
                      <div className={cn("px-3 py-1.5 border-t flex items-center gap-2", isDup ? "bg-amber-50 border-amber-100" : "bg-blue-50 border-blue-100")}>
                        {isDup && (
                          <Badge variant="outline" className="text-[8px] px-1 h-3.5 border-amber-300 text-amber-700 bg-amber-100" data-testid={`dup-match-badge-${f.file_id}`}>
                            Duplicate patient matched
                          </Badge>
                        )}
                        <span className={cn("text-[10px]", isDup ? "text-amber-600" : "text-blue-600")}>
                          {isDup ? 'Matched Patient:' : 'Linked Patient ID:'}
                        </span>
                        <span className={cn("text-[10px] font-mono", isDup ? "text-amber-800" : "text-blue-800")} data-testid={`patient-id-${f.file_id}`}>{pid}</span>
                        {lr?.display_name && <span className="text-[10px] text-slate-500">({lr.display_name})</span>}
                        {lr?.match_reason && <span className="text-[10px] text-slate-400 ml-auto">{lr.match_reason}</span>}
                      </div>
                    );
                    if (linked) return (
                      <div className="px-3 py-1.5 bg-amber-50 border-t border-amber-100 flex items-center gap-2">
                        <span className="text-[10px] text-amber-600">Linked Patient ID:</span>
                        <span className="text-[10px] italic text-amber-500" data-testid={`patient-id-${f.file_id}`}>not resolved</span>
                      </div>
                    );
                    return null;
                  })()}
                  {/* ── Absorption controls ── */}
                  {(() => {
                    const absRec = absorptionMap[f.file_id];
                    const isAbsorbed = absRec?.status === 'absorbed';
                    const isParsed = f.status === 'completed';
                    const resolvedPid = getLinkedPatientId(f, lr);
                    const linked = lr && isLinkedStatus(lr.linkage_status);
                    const hasPatient = !!resolvedPid;
                    const isEligible = isParsed && hasPatient;
                    const isLoading = absorbingId === f.file_id;
                    const ccdaBid = ccdaViewRecord?.batch_id || ccdaViewRecord?.id;
                    let disabledReason = '';
                    if (!isParsed) disabledReason = 'Not parsed yet';
                    else if (linked && !hasPatient) disabledReason = 'Patient ID not resolved';
                    else if (!hasPatient) disabledReason = 'No linked patient';

                    return (
                      <div className="px-3 py-1.5 border-t border-slate-100 flex items-center justify-between" data-testid={`absorption-row-${f.file_id}`}>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-slate-500">Absorption:</span>
                          {isAbsorbed ? (
                            <Badge variant="outline" className="text-[9px] px-1.5 h-4 border-green-300 text-green-700 bg-green-50" data-testid={`abs-badge-${f.file_id}`}>
                              <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Absorbed
                            </Badge>
                          ) : isEligible ? (
                            <Badge variant="outline" className="text-[9px] px-1.5 h-4 border-slate-200 text-slate-500" data-testid={`abs-badge-${f.file_id}`}>
                              Not Absorbed
                            </Badge>
                          ) : (
                            <span className="text-[9px] text-slate-400 italic flex items-center gap-0.5" title={disabledReason} data-testid={`abs-badge-${f.file_id}`}>
                              <AlertTriangle className="h-2.5 w-2.5" /> {disabledReason}
                            </span>
                          )}
                          {isAbsorbed && absRec?.id_source === 'ccda_file_adapter' && (
                            <span className="text-[8px] text-slate-400">(adapter: ccda file)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {isAbsorbed ? (
                            <>
                            <Button variant="ghost" size="sm" className="h-6 text-[10px] text-teal-600 hover:text-teal-800 gap-0.5 px-1.5"
                              onClick={(e) => { e.stopPropagation(); handleViewChart(resolvedPid, buildResolverCtx(f, ccdaBid)); }}
                              data-testid={`view-chart-btn-${f.file_id}`}>
                              <Eye className="h-2.5 w-2.5" /> View Chart
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-500 hover:text-red-700 gap-0.5 px-1.5"
                              disabled={isLoading}
                              onClick={(e) => { e.stopPropagation(); setUnabsorbConfirmId(f.file_id); }}
                              data-testid={`unabsorb-btn-${f.file_id}`}>
                              {isLoading ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Undo2 className="h-2.5 w-2.5" />}
                              Unabsorb
                            </Button>
                            </>
                          ) : isEligible ? (
                            <Button variant="outline" size="sm" className="h-6 text-[10px] gap-0.5 px-2 border-slate-300"
                              disabled={isLoading}
                              onClick={(e) => { e.stopPropagation(); handleAbsorb(f.file_id, resolvedPid, ccdaBid, buildResolverCtx(f, ccdaBid)); }}
                              data-testid={`absorb-btn-${f.file_id}`}>
                              {isLoading ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <ArrowUpToLine className="h-2.5 w-2.5" />}
                              Absorb to Chart
                            </Button>
                          ) : (
                            <Button variant="outline" size="sm" className="h-6 text-[10px] gap-0.5 px-2 opacity-40 cursor-not-allowed" disabled
                              title={disabledReason} data-testid={`absorb-btn-disabled-${f.file_id}`}>
                              <ArrowUpToLine className="h-2.5 w-2.5" /> Absorb to Chart
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  {lr?.duplicate_reason && (
                    <div className="px-3 py-1.5 bg-amber-50 border-t border-amber-100">
                      <span className="text-[10px] text-amber-700">{lr.duplicate_reason}</span>
                    </div>
                  )}
                  {f.error && (
                    <div className="px-3 py-2 bg-red-50 border-t border-red-100">
                      <p className="text-xs text-red-600">{f.error}</p>
                    </div>
                  )}
                  {f.extracted && (
                    <div className="px-3 py-2 space-y-2 border-t border-slate-100">
                      {f.extracted.demographics?.name && (
                        <div style={{ background: '#f9fafb', borderRadius: 8, padding: '8px 12px' }}>
                          <p style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>{f.extracted.demographics.name}</p>
                          <p style={{ fontSize: 11, color: '#888' }}>
                            {f.extracted.demographics.gender} · DOB: {f.extracted.demographics.dob || '—'} · {f.extracted.demographics.member_id || '—'}
                          </p>
                        </div>
                      )}
                      {f.extracted.problems?.length > 0 && (
                        <div>
                          <p style={{ fontSize: 11, fontWeight: 500, color: '#555', marginBottom: 4 }}>Problems ({f.extracted.problems.length})</p>
                          {f.extracted.problems.map((p, pi) => (
                            <p key={pi} style={{ fontSize: 12, color: '#333', padding: '2px 0' }}>{p.code} — {p.display}</p>
                          ))}
                        </div>
                      )}
                      {f.extracted.medications?.length > 0 && (
                        <div>
                          <p style={{ fontSize: 11, fontWeight: 500, color: '#555', marginBottom: 4 }}>Medications ({f.extracted.medications.length})</p>
                          {f.extracted.medications.map((m, mi) => (
                            <p key={mi} style={{ fontSize: 12, color: '#333', padding: '2px 0' }}>{m.name} — {m.route} {m.dose ? `(${m.dose})` : ''}</p>
                          ))}
                        </div>
                      )}
                      {f.extracted.allergies?.length > 0 && (
                        <div>
                          <p style={{ fontSize: 11, fontWeight: 500, color: '#555', marginBottom: 4 }}>Allergies ({f.extracted.allergies.length})</p>
                          {f.extracted.allergies.map((a, ai) => (
                            <p key={ai} style={{ fontSize: 12, color: '#333', padding: '2px 0' }}>{a.substance} — {a.reaction}</p>
                          ))}
                        </div>
                      )}
                      {f.extracted.vitals?.length > 0 && (
                        <div>
                          <p style={{ fontSize: 11, fontWeight: 500, color: '#555', marginBottom: 4 }}>Vitals ({f.extracted.vitals.length})</p>
                          {f.extracted.vitals.map((v, vi) => (
                            <p key={vi} style={{ fontSize: 12, color: '#333', padding: '2px 0' }}>{v.name}: {v.value} {v.unit}</p>
                          ))}
                        </div>
                      )}
                      {f.extracted.immunizations?.length > 0 && (
                        <div>
                          <p style={{ fontSize: 11, fontWeight: 500, color: '#555', marginBottom: 4 }}>Immunizations ({f.extracted.immunizations.length})</p>
                          {f.extracted.immunizations.map((im, imi) => (
                            <p key={imi} style={{ fontSize: 12, color: '#333', padding: '2px 0' }}>{im.name} — {im.date}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {f.log?.length > 0 && (
                    <div className="px-3 py-2 bg-slate-50 border-t border-slate-100">
                      <p style={{ fontSize: 10, fontWeight: 500, color: '#888', marginBottom: 2 }}>Ingestion Log</p>
                      {f.log.map((line, li) => (
                        <p key={li} style={{ fontSize: 10, color: '#999', fontFamily: 'monospace' }}>{line}</p>
                      ))}
                    </div>
                  )}
                </div>
              );})}
            </div>
          ) : (
            <div className="py-6 text-center text-sm text-slate-400">No file details available</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Provider Modal */}
      <Dialog open={showAddProvider} onOpenChange={setShowAddProvider}>
        <DialogContent className="max-w-md" data-testid="add-provider-modal">
          <DialogHeader>
            <DialogTitle>Add Provider</DialogTitle>
            <DialogDescription>Add a new provider to your clinic</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>First Name</Label><Input data-testid="provider-first-name" /></div>
              <div className="space-y-2"><Label>Last Name</Label><Input data-testid="provider-last-name" /></div>
            </div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" data-testid="provider-email" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Specialty</Label>
                <Select>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal_medicine">Internal Medicine</SelectItem>
                    <SelectItem value="pain_management">Pain Management</SelectItem>
                    <SelectItem value="cardiology">Cardiology</SelectItem>
                    <SelectItem value="orthopedics">Orthopedics</SelectItem>
                    <SelectItem value="psychiatry">Psychiatry</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Monthly Credit Limit</Label><Input type="number" defaultValue="1000" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddProvider(false)}>Cancel</Button>
            <Button className="bg-black hover:bg-gray-800 gap-2" onClick={() => { toast.success('Provider added'); setShowAddProvider(false); }} data-testid="submit-provider-btn">
              <Plus className="h-4 w-4" /> Add Provider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Absorption History Modal */}
      <Dialog open={absHistoryOpen} onOpenChange={(v) => { if (!v) { setAbsHistoryOpen(false); setAbsBatchDetail(null); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="absorption-history-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4" /> Absorption History
            </DialogTitle>
            <DialogDescription>
              All document-to-chart absorption batches
            </DialogDescription>
          </DialogHeader>
          {absBatchDetail ? (
            <div className="space-y-3 mt-2">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setAbsBatchDetail(null)}>
                  &larr; Back to list
                </Button>
                <span className="text-[10px] font-mono text-slate-500">{absBatchDetail.batch_id}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={cn('text-[9px] px-1.5 h-4',
                  absBatchDetail.status === 'completed' ? 'border-green-200 text-green-700 bg-green-50' :
                  absBatchDetail.status === 'unabsorbed' ? 'border-amber-200 text-amber-700 bg-amber-50' :
                  'border-slate-200 text-slate-500'
                )}>{absBatchDetail.status}</Badge>
                <span className="text-[10px] text-slate-500">
                  {absBatchDetail.created_at ? new Date(absBatchDetail.created_at).toLocaleString() : '—'}
                </span>
                <span className="text-[10px] text-slate-400">Source: {absBatchDetail.source || '—'}</span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Analysis ID</TableHead>
                    <TableHead className="text-xs">ID Source</TableHead>
                    <TableHead className="text-xs">Patient ID</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Sections</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(absBatchDetail.items || []).map((item, idx) => (
                    <TableRow key={idx} data-testid={`abs-detail-item-${idx}`}>
                      <TableCell className="text-[10px] font-mono text-slate-600">{item.analysis_id}</TableCell>
                      <TableCell className="text-[10px] text-slate-500">{item.id_source || '—'}</TableCell>
                      <TableCell className="text-[10px] font-mono text-blue-700">{item.patient_id || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('text-[9px] px-1 h-4',
                          item.status === 'absorbed' ? 'border-green-200 text-green-700 bg-green-50' :
                          item.status === 'unabsorbed' ? 'border-amber-200 text-amber-700 bg-amber-50' :
                          item.status === 'failed' ? 'border-red-200 text-red-700 bg-red-50' :
                          'border-slate-200 text-slate-500'
                        )}>{item.status}</Badge>
                      </TableCell>
                      <TableCell className="text-[10px] text-slate-500">
                        {(item.sections || []).length > 0
                          ? item.sections.map(s => typeof s === 'string' ? s : s.section).join(', ')
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : absLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 text-slate-400 animate-spin" /></div>
          ) : absBatches.length === 0 ? (
            <div className="text-center py-8">
              <ArrowUpToLine className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No absorption records yet</p>
              <p className="text-xs text-slate-400 mt-1">Absorb a parsed C-CDA file to see history here</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Batch ID</TableHead>
                  <TableHead className="text-xs">Created</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Total</TableHead>
                  <TableHead className="text-xs">Success</TableHead>
                  <TableHead className="text-xs">Failed</TableHead>
                  <TableHead className="text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {absBatches.map((b) => {
                  const bid = b.batch_id || b.id;
                  return (
                    <TableRow key={bid} data-testid={`abs-history-row-${bid}`}>
                      <TableCell className="text-[10px] font-mono text-slate-600">{bid}</TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {b.created_at ? new Date(b.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('text-[9px] px-1 h-4',
                          b.status === 'completed' ? 'border-green-200 text-green-700 bg-green-50' :
                          b.status === 'unabsorbed' ? 'border-amber-200 text-amber-700 bg-amber-50' :
                          b.status === 'failed' ? 'border-red-200 text-red-700 bg-red-50' :
                          b.status === 'processing' ? 'border-yellow-200 text-yellow-700 bg-yellow-50' :
                          'border-slate-200 text-slate-500'
                        )}>{b.status}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">{b.total ?? 0}</TableCell>
                      <TableCell className="text-xs text-green-700">{b.success ?? 0}</TableCell>
                      <TableCell className="text-xs text-red-600">{b.failed ?? 0}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                          onClick={() => fetchAbsBatchDetail(bid)} data-testid={`abs-history-view-${bid}`}>
                          <Eye className="h-3.5 w-3.5 text-slate-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Unabsorb Confirmation Dialog */}
      <Dialog open={!!unabsorbConfirmId} onOpenChange={(v) => !v && setUnabsorbConfirmId(null)}>
        <DialogContent className="max-w-sm" data-testid="unabsorb-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" /> Confirm Unabsorb
            </DialogTitle>
            <DialogDescription>
              This will remove the absorbed clinical data from the patient's chart for file <span className="font-mono">{unabsorbConfirmId}</span>. This action can be reversed by re-absorbing.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnabsorbConfirmId(null)} data-testid="unabsorb-cancel-btn">Cancel</Button>
            <Button variant="destructive" className="gap-1"
              onClick={() => handleUnabsorb(unabsorbConfirmId)}
              data-testid="unabsorb-confirm-btn">
              <Undo2 className="h-3.5 w-3.5" /> Unabsorb
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LuminaSettings;
